import React, { ChangeEvent, Dispatch, FormEvent, useCallback, useEffect, useReducer, useRef } from 'react';
import classnames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';

import {
  Button,
  Container,
  Typography,
  FormControl,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  Icon,
  TextField,
  Translate,
  NotificationBanner
} from '@hydra/common/components';
import { useI18n } from '@hydra/common/customHooks';

import * as fetcher from '@hydra/utils/fetcher';
import { decryptSecret, encryptSecret } from '@hydra/utils/extensionSecurityHelper';
import settings from '@hydra/utils/settings';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: theme.spacing(4),
    width: '100%'
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(5, 4),
    width: '600px',
    background: theme.palette.background.paper,
    boxShadow: `0px 2px 4px ${theme.palette.grey[200]}`,
    margin: `${theme.spacing(6)}px auto`,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      padding: theme.spacing(2.5, 2)
    }
  },
  button: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1.5)
  },
  buttonText: { position: 'relative' },
  loader: {
    position: 'absolute',
    left: theme.spacing(-3),
    top: theme.spacing(0.75)
  },
  alert: {
    width: '100%',
    marginBottom: theme.spacing(2)
  }
}));

function EndAdornment ({ handleClick }: { handleClick: (event: ChangeEvent<any>) => void }) {
  return (
    <InputAdornment position='end'>
      <IconButton size='small' onClick={handleClick} tabIndex='-1'>
        <Icon type='cross' size='sm' color='secondary' />
      </IconButton>
    </InputAdornment>
  );
}
interface Field {
  id: string,
  label: string,
  required?: boolean,
  minLength?: number,
  touched: boolean,
  valid: boolean,
  errorMessage?: string,
  value: string
}

interface State {
  fields: {
    [key: string]: Field
  },
  valid: boolean,
  alert?: {
    severity: 'error' | 'info' | 'success' | 'warning',
    message: string
  },
  loading: boolean
}

export default function ResetPasswordPage ({ match: { params } }: { match: { params: { lang: string, regenerationKey: string } } }) {
  const { regenerationKey } = params;
  const { locale, translate } = useI18n();
  const classes = useStyles();
  const recaptchaRef = useRef<any | null>();
  const recaptchaSiteKey = settings.get('recaptchaSiteKey');

  const validateField = (state: State, fieldName: string, value: any): { valid: boolean, errorMessage?: string } => {
    const field: Field = state.fields[fieldName];

    if (field.required && !value) {
      return {
        valid: false,
        errorMessage: translate('validator_required', {
          with: {
            field: field.label
          }
        })
      };
    } else if (field.minLength && value && value.length < field.minLength) {
      return {
        valid: false,
        errorMessage: translate('validator_min_length', {
          with: {
            field: field.label,
            minLength: field.minLength
          }
        })
      };
    }

    return {
      valid: true
    };
  };

  const validateForm = useCallback((state: State): void => {
    let valid = Object.getOwnPropertyNames(state.fields).every(f => {
      const field = state.fields[f];

      return field.valid;
    });

    const password = state.fields.password;
    const passwordConfirmation = state.fields.passwordConfirmation;
    const passwordVerified = !!passwordConfirmation.value && passwordConfirmation.value === password.value;

    if (passwordVerified !== passwordConfirmation.valid) {
      setFieldValidity('passwordConfirmation', passwordVerified, translate('validator_password_verification'));
    }

    if (!passwordVerified) {
      valid = false;
    }

    if (state.valid !== valid) {
      dispatch({ type: 'SET_FORM_VALIDITY', valid: valid });
    }
  }, [translate]);

  const reducer = (previousState: State, action: any): State => {
    switch (action.type) {
      case 'RESET_FORM': {
        const newState = {
          ...previousState,
          valid: false
        };

        Object.keys(newState.fields).forEach(fieldName => {
          const field = newState.fields[fieldName];

          field.touched = false;
          field.valid = false;
          field.errorMessage = undefined;
          field.value = '';
        });

        return newState;
      }
      case 'SET_ALERT':
        return {
          ...previousState,
          alert: action.severity && action.message
            ? {
                severity: action.severity,
                message: action.message
              }
            : undefined
        };
      case 'SET_FIELD_VALIDITY': {
        const field: Field = previousState.fields[action.field];

        return {
          ...previousState,
          fields: {
            ...previousState.fields,
            [action.field]: {
              ...field,
              valid: action.valid,
              errorMessage: action.errorMessage
            }
          }
        };
      }
      case 'SET_FIELD_VALUE': {
        const field: Field = previousState.fields[action.field];
        const validation = validateField(previousState, action.field, action.value);

        return {
          ...previousState,
          fields: {
            ...previousState.fields,
            [action.field]: {
              ...field,
              touched: true,
              valid: validation.valid,
              errorMessage: validation.errorMessage,
              value: action.value
            }
          }
        };
      }
      case 'SET_FORM_ERRORS': {
        const newState = {
          ...previousState,
          valid: false
        };

        const fieldErrors = action.fieldErrors;

        if (fieldErrors) {
          Object.keys(fieldErrors).forEach(fieldName => {
            const field = newState.fields[fieldName];
            const error = fieldErrors[fieldName];

            if (field && error) {
              field.valid = false;
              field.errorMessage = translate(error.message, {
                with: {
                  field: fieldName,
                  ...error
                }
              });
            }
          });
        }

        return newState;
      }
      case 'SET_FORM_VALIDITY':
        return {
          ...previousState,
          valid: action.valid
        };
      case 'SET_LOADING':
        return {
          ...previousState,
          loading: action.loading
        };
      default:
        throw new Error(`Unsupported action type: ${action.type}`);
    }
  };

  const [state, dispatch]: [state: State, dispatch: Dispatch<any>] = useReducer(reducer, {
    fields: {
      password: {
        id: 'password',
        label: translate('Password'),
        required: true,
        minLength: 6,
        touched: false,
        valid: false,
        value: ''
      },
      passwordConfirmation: {
        id: 'password-confirmation',
        label: translate('authentication_confirm_new_password'),
        required: true,
        touched: false,
        valid: false,
        value: ''
      }
    },
    valid: false,
    loading: false
  });

  useEffect(() => {
    validateForm(state);
  }, [validateForm, state]);

  const resetFieldValue = (fieldName: string) => {
    dispatch({ type: 'SET_FIELD_VALUE', field: fieldName, value: '' });
  };

  const handleFieldChange = (event: ChangeEvent<any>, fieldName: string) => {
    dispatch({ type: 'SET_FIELD_VALUE', field: fieldName, value: event.target.value });
  };

  const handleSubmit = async (event: FormEvent): Promise<any> => {
    event.preventDefault();

    if (!state.valid) {
      return;
    }

    try {
      setAlert();
      const token = await recaptchaRef.current.executeAsync();
      setLoading(true);

      const resetCredentialObject = {
        password: state.fields.password.value,
        passwordConfirmation: state.fields.passwordConfirmation.value,
        timestamp: Date.now()
      };

      const user = await fetcher.post('/api/user/reset-password', {
        json: {
          regenerationKey,
          RESET_CREDENTIALS: encryptSecret(JSON.stringify(resetCredentialObject))
        }
      }).json();
      const { USER_CREDENTIALS } = user;
      const decriptedUserObject = JSON.parse(decryptSecret(USER_CREDENTIALS));

      const loginCredentialObject = {
        email: decriptedUserObject.email,
        password: state.fields.password.value,
        timestamp: Date.now()
      };
      await fetcher.post('/api/user/login', {
        json: {
          LOGIN_CREDENTIALS: encryptSecret(JSON.stringify(loginCredentialObject)),
          'g-recaptcha-response': token
        }
      });

      resetForm();
      setLoading(false);
      window.location.href = `/${locale}/overview`;
    } catch (error: any) {
      let details: any;

      if (error instanceof fetcher.HTTPError) {
        details = await error.response.json();
      }

      if (details?.fields && Object.keys(details.fields).length) {
        setFormErrors(details.fields);
      } else if (details?.error && typeof details?.error === 'string') {
        setAlert('error', translate(details.error));
      } else {
        Sentry.captureException(error);
        setAlert('error', translate('generic_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const setAlert = (severity?: 'error' | 'info' | 'success' | 'warning', message?: string) => {
    dispatch({ type: 'SET_ALERT', severity: severity, message: message });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', loading: loading });
  };

  const setFieldValidity = (fieldName: string, valid: boolean, errorMessage?: string) => {
    dispatch({ type: 'SET_FIELD_VALIDITY', field: fieldName, valid: valid, errorMessage: errorMessage });
  };

  const setFormErrors = (fieldErrors: { [fieldName: string]: string }) => {
    dispatch({ type: 'SET_FORM_ERRORS', fieldErrors: fieldErrors });
  };

  const resetForm = () => {
    dispatch({ type: 'RESET_FORM' });
  };

  const textInput = (fieldName: string, type: string = 'text') => {
    const field: Field = state.fields[fieldName];

    if (!field) {
      return null;
    }

    return (
      <FormControl>
        <TextField
          label={field.label}
          id={`change-password-${field.id}`}
          type={type}
          value={field.value}
          required={field.required ?? false}
          error={field.touched && !field.valid}
          helperText={field.touched && !field.valid && field.errorMessage ? field.errorMessage : null}
          onChange={(event: ChangeEvent<any>) => handleFieldChange(event, fieldName)}
          InputProps={{
            endAdornment: field.value && <EndAdornment handleClick={() => resetFieldValue(fieldName)} />
          }}
        />
      </FormControl>
    );
  };

  return (
    <Container>
      <div className={classes.root}>
        <Typography
          variant='h3'
          color='textSecondary'
          className={classes.title}
        >
          <Translate content='change_password' />
        </Typography>
        {state.alert?.message && (
          <div className={classes.alert}>
            <NotificationBanner severity={state.alert.severity} title={state.alert.message} dismissable />
          </div>
        )}
        <FormControl
          component='form'
          onSubmit={handleSubmit}
          fullWidth
        >

          <ReCAPTCHA
            ref={recaptchaRef}
            size='invisible'
            sitekey={recaptchaSiteKey}
          />
          {textInput('password', 'password')}
          {textInput('passwordConfirmation', 'password')}
          <Button
            disabled={!state.valid && state.loading}
            variant='contained'
            color='primary'
            size='large'
            type='submit'
            className={classnames(classes.button)}
          >
            <div className={classes.buttonText}>
              {state.loading && <Icon type='loader' size='sm' color='white' className={classes.loader} />}
              <Translate content='send_password_button' />
            </div>
          </Button>
        </FormControl>
      </div>
    </Container>
  );
}
